/** Node ids are UUIDv7 strings: time-ordered, generated on the client so offline creation works. */
export type NodeId = string

const HEX = '0123456789abcdef'

function hex(bytes: Uint8Array): string {
  let s = ''
  for (const b of bytes) s += HEX[b >> 4]! + HEX[b & 15]!
  return s
}

/**
 * RFC 9562 UUIDv7: 48-bit unix ms timestamp, version 7, 74 random bits.
 * `random` is injectable so tests stay deterministic.
 */
export function uuidv7(now: number = Date.now(), random?: (n: number) => Uint8Array): NodeId {
  const b = random ? random(16) : crypto.getRandomValues(new Uint8Array(16))
  let ms = Math.floor(now)
  for (let i = 5; i >= 0; i--) {
    b[i] = ms % 256
    ms = Math.floor(ms / 256)
  }
  b[6] = (b[6]! & 0x0f) | 0x70 // version 7
  b[8] = (b[8]! & 0x3f) | 0x80 // variant 10
  const h = hex(b)
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** Deterministic id source for tests and fuzzing: n1, n2, … with a prefix. */
export function counterIds(prefix = 'n'): () => NodeId {
  let i = 0
  return () => `${prefix}${++i}`
}

/**
 * Journal days have deterministic ids derived from their date, so two devices that both start
 * today's page create the same node (the server treats the second create as a no-op).
 * Format: a valid UUID (version 8, custom) with the date digits in the last group.
 */
export function journalId(date: string): NodeId {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error(`journalId: bad date ${date}`)
  return `50a1a400-0000-8000-8000-0000${date.replace(/-/g, '')}`
}
